import { Component, input, output, signal } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'dbx-file-upload',
  standalone: true,
  imports: [MatButton, MatIcon, TranslateModule, MatIconButton],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent {
  acceptedFileTypes = input<string[]>(['*/*']);
  maxFileSizeMB = input<number>(10);
  multiple = input<boolean>(false);
  hideSelectedFiles = input<boolean>(false);

  filesSelected = output<File[]>();

  protected isDragging = signal<boolean>(false);
  protected errorMessage = signal<string | null>(null);
  protected selectedFiles = signal<File[]>([]);

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
    }
  }

  private handleFiles(files: File[]): void {
    this.errorMessage.set(null);

    // Validate multiple files
    if (!this.multiple() && files.length > 1) {
      this.errorMessage.set('fileUpload.error.singleFileOnly');
      return;
    }

    // Validate file types and size
    const validFiles: File[] = [];
    for (const file of files) {
      if (!this.isValidFileType(file, this.acceptedFileTypes())) {
        this.errorMessage.set('fileUpload.error.invalidFileType');
        return;
      }

      if (!this.isValidFileSize(file, this.maxFileSizeMB())) {
        this.errorMessage.set('fileUpload.error.fileTooLarge');
        return;
      }

      validFiles.push(file);
    }

    this.selectedFiles.set(validFiles);
    this.filesSelected.emit(validFiles);
  }

  private isValidFileType(file: File, acceptedTypes?: string[]): boolean {
    if (!acceptedTypes || acceptedTypes.includes('*/*')) {
      return true;
    }

    return acceptedTypes.some((type) => {
      if (type.endsWith('/*')) {
        const category = type.split('/')[0];
        return file.type.startsWith(category + '/');
      }
      return file.type === type;
    });
  }

  private isValidFileSize(file: File, maxSizeMB?: number): boolean {
    if (!maxSizeMB) {
      return true;
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  removeFile(index: number): void {
    const files = this.selectedFiles();
    files.splice(index, 1);
    this.selectedFiles.set([...files]);
    this.filesSelected.emit(files);
  }

  get acceptAttribute(): string {
    const types = this.acceptedFileTypes();
    return types && types.length > 0 ? types.join(',') : '*/*';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}
